import { useState } from 'react';
import { 
  Calendar, Clock, Video, Mic, MapPin, 
  CheckCircle, AlertCircle, BookOpen,
  Phone, MessageSquare
} from 'lucide-react';
import { Button } from '@zaogig/ui/button';
import { Card, CardContent } from '@zaogig/ui/card';
import { Badge } from '@zaogig/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@zaogig/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@zaogig/ui/dialog';
import { Progress } from '@zaogig/ui/progress';
import { useVoiceRecorder } from '../hooks/use-voice';

import { toast } from 'sonner';
import { InterviewPreparation } from '../components/InterviewPreparation';
import type { Job } from '@zaogig/types';

interface Interview {
  id: string;
  jobId: string;
  status: string;
  scheduledAt: string;
  duration: number;
  type: 'video' | 'voice' | 'onsite';
  notes?: string;
  voiceAuthRequired?: boolean;
  result?: string;
}

interface InterviewPageProps {
  interviews: Interview[];
  jobs: Job[];
}

export default function InterviewPage({ interviews, jobs }: InterviewPageProps) {
  const [activeTab, setActiveTab] = useState('upcoming');
  const [selectedInterview, setSelectedInterview] = useState<Interview | null>(null);
  const [showVoiceAuth, setShowVoiceAuth] = useState(false);
  const [showPreparation, setShowPreparation] = useState(false);
  const [authStep, setAuthStep] = useState<'intro' | 'recording' | 'success'>('intro');

  // 录音Hook
  const { isRecording, recordingTime, startRecording, stopRecording } = useVoiceRecorder({
    maxDuration: 5000,
    onComplete: () => {
      setAuthStep('success');
      setTimeout(() => {
        setShowVoiceAuth(false);
        setAuthStep('intro');
        toast.success('声纹认证通过！');
      }, 1500);
    }
  });

  const upcomingInterviews = interviews.filter(i => i.status === 'scheduled');
  const completedInterviews = interviews.filter(i => i.status === 'completed');

  const getJobById = (jobId: string) => jobs.find(j => j.id === jobId);

  const handleStartInterview = (interview: Interview) => {
    if (interview.voiceAuthRequired) {
      setSelectedInterview(interview);
      setShowVoiceAuth(true);
    } else {
      toast.info('进入面试房间...');
    }
  };

  const handleStartVoiceAuth = async () => {
    try {
      await startRecording();
      setAuthStep('recording');
    } catch (error) {
      toast.error('无法启动录音');
    }
  };

  return (
    <div className="wx-page">
      {/* 顶部标题区 */}
      <div className="bg-white px-4 py-4 border-b border-gray-100">
        <h1 className="text-xl font-bold text-gray-800">我的面试</h1>
        <p className="text-sm text-gray-500 mt-1">
          您有 <span className="text-blue-600 font-medium">{upcomingInterviews.length}</span> 个待面试
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-2 bg-white p-0 h-auto">
          <TabsTrigger 
            value="upcoming" 
            className="py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:bg-transparent"
          >
            待面试 ({upcomingInterviews.length})
          </TabsTrigger>
          <TabsTrigger 
            value="completed"
            className="py-3 rounded-none border-b-2 border-transparent data-[state=active]:border-blue-500 data-[state=active]:bg-transparent"
          >
            已完成 ({completedInterviews.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="upcoming" className="px-4 py-4 mt-0">
          {upcomingInterviews.length === 0 ? (
            <EmptyState type="upcoming" />
          ) : (
            upcomingInterviews.map((interview) => (
              <InterviewCard 
                key={interview.id}
                interview={interview}
                job={getJobById(interview.jobId)}
                type="upcoming"
                onClick={() => setSelectedInterview(interview)}
                onStartInterview={() => handleStartInterview(interview)}
              />
            ))
          )}
        </TabsContent>

        <TabsContent value="completed" className="px-4 py-4 mt-0">
          {completedInterviews.length === 0 ? (
            <EmptyState type="completed" />
          ) : (
            completedInterviews.map((interview) => (
              <InterviewCard 
                key={interview.id}
                interview={interview}
                job={getJobById(interview.jobId)}
                type="completed"
              />
            ))
          )}
        </TabsContent>
      </Tabs>

      {/* 面试详情弹窗 */}
      <InterviewDetailDialog 
        interview={selectedInterview || undefined}
        job={selectedInterview ? getJobById(selectedInterview.jobId) : undefined}
        isOpen={!!selectedInterview && !showVoiceAuth}
        onClose={() => setSelectedInterview(null)}
        onStartInterview={() => selectedInterview && handleStartInterview(selectedInterview)}
        onShowPreparation={() => {
          setShowPreparation(true);
          setSelectedInterview(null);
        }}
      />

      {/* 面试准备材料弹窗 */}
      {showPreparation && selectedInterview && (
        <Dialog open={showPreparation} onOpenChange={setShowPreparation}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden p-0">
            <div className="h-full">
              <InterviewPreparation 
                jobId={selectedInterview.jobId} 
                interviewId={selectedInterview.id}
              />
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* 声纹认证弹窗 */}
      <VoiceAuthDialog 
        isOpen={showVoiceAuth}
        onClose={() => {
          setShowVoiceAuth(false);
          setAuthStep('intro');
          if (isRecording) stopRecording();
        }}
        step={authStep}
        onStartRecording={handleStartVoiceAuth}
        recordingTime={recordingTime}
      />
    </div>
  );
}

// 面试卡片
interface InterviewCardProps {
  interview: Interview;
  job?: Job;
  type: 'upcoming' | 'completed';
  onClick?: () => void;
  onStartInterview?: () => void;
}

function InterviewCard({ interview, job, type, onClick, onStartInterview }: InterviewCardProps) {
  if (!job) return null;

  const isUpcoming = type === 'upcoming';

  return (
    <Card 
      className={`wx-card mb-3 ${isUpcoming ? 'cursor-pointer' : 'opacity-70'}`}
      onClick={onClick}
    >
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="font-semibold text-gray-800 mb-1">{job.title}</h3>
            <p className="text-sm text-gray-500">{job.company.name}</p>
          </div>
          {isUpcoming ? (
            <Badge className={interview.voiceAuthRequired ? 'wx-badge-blue' : 'wx-badge-green'}>
              {interview.voiceAuthRequired ? '声纹认证' : '普通面试'}
            </Badge>
          ) : (
            <ResultBadge result={interview.result} />
          )}
        </div>

        <div className="space-y-2 mb-3">
          <InfoRow 
            icon={<Calendar className="w-4 h-4 text-blue-500" />} 
            text={interview.scheduledAt}
          />
          <InfoRow 
            icon={<Clock className="w-4 h-4 text-blue-500" />} 
            text={`预计 ${interview.duration} 分钟`}
          />
          <InfoRow 
            icon={getInterviewTypeIcon(interview.type)} 
            text={getInterviewTypeText(interview.type)}
          />
        </div>

        {isUpcoming && interview.notes && (
          <div className="bg-yellow-50 rounded-lg p-3 mb-3">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-yellow-700">{interview.notes}</p>
            </div>
          </div>
        )}

        {isUpcoming && onStartInterview && (
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              size="sm"
              className="flex-1"
              onClick={(e) => {
                e.stopPropagation();
                toast.info('请查看岗位详情中的企业联系电话');
              }}
            >
              <Phone className="w-4 h-4 mr-1" />
              联系企业
            </Button>
            <Button 
              size="sm"
              className="flex-1 bg-blue-500 hover:bg-blue-600"
              onClick={(e) => {
                e.stopPropagation();
                onStartInterview();
              }}
            >
              <Video className="w-4 h-4 mr-1" />
              进入面试
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// 结果标签
function ResultBadge({ result }: { result?: string }) {
  const config = {
    passed: { class: 'wx-badge-green', text: '已通过' },
    rejected: { class: 'wx-badge-red', text: '未通过' },
    pending: { class: 'wx-badge-orange', text: '待反馈' }
  };
  
  const { class: className, text } = config[result as keyof typeof config] || config.pending;
  
  return <Badge className={className}>{text}</Badge>;
}

// 信息行
function InfoRow({ icon, text }: { icon: React.ReactNode; text: string }) {
  return (
    <div className="flex items-center gap-2 text-sm text-gray-600">
      {icon}
      <span>{text}</span>
    </div>
  );
}

// 面试详情弹窗
interface InterviewDetailDialogProps {
  interview?: Interview;
  job?: Job;
  isOpen: boolean;
  onClose: () => void;
  onStartInterview: () => void;
  onShowPreparation: () => void;
}

function InterviewDetailDialog({ 
  interview, 
  job, 
  isOpen, 
  onClose, 
  onStartInterview,
  onShowPreparation
}: InterviewDetailDialogProps) {
  if (!interview || !job) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-left">面试详情</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* 岗位信息 */}
          <div className="p-4 bg-gray-50 rounded-lg">
            <h3 className="font-semibold text-gray-800 mb-1">{job.title}</h3>
            <p className="text-sm text-gray-500">{job.company.name}</p>
          </div>

          {/* 面试信息 */}
          <div className="space-y-3">
            <InfoCard 
              icon={<Calendar className="w-5 h-5 text-blue-600" />}
              label="面试时间"
              value={interview.scheduledAt}
              bgColor="bg-blue-100"
            />
            <InfoCard 
              icon={<Clock className="w-5 h-5 text-green-600" />}
              label="预计时长"
              value={`${interview.duration} 分钟`}
              bgColor="bg-green-100"
            />
            <InfoCard 
              icon={getInterviewTypeIcon(interview.type)}
              label="面试方式"
              value={getInterviewTypeText(interview.type)}
              bgColor="bg-purple-100"
            />
          </div>

          {/* 声纹认证提示 */}
          {interview.voiceAuthRequired && (
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mic className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="font-medium text-gray-800 mb-1">声纹认证面试</p>
                  <p className="text-sm text-gray-600">
                    本次面试需要进行声纹身份认证，确保面试者为本人。
                    系统将在面试开始前进行声纹验证。
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* 注意事项 */}
          <div>
            <h4 className="font-semibold text-gray-800 mb-2">注意事项</h4>
            <ul className="space-y-2 text-sm text-gray-600">
              <CheckItem text="请提前5分钟进入面试房间" />
              <CheckItem text="确保网络畅通，环境安静" />
              <CheckItem text="准备好身份证和相关证书" />
              {interview.voiceAuthRequired && (
                <CheckItem text="确保麦克风正常工作" />
              )}
            </ul>
          </div>

          {/* 操作按钮 */}
          <div className="space-y-3 pt-4 border-t border-gray-100">
            <Button 
              variant="outline"
              className="w-full"
              onClick={onShowPreparation}
            >
              <BookOpen className="w-4 h-4 mr-2" />
              查看面试准备材料
            </Button>
            <div className="flex gap-3">
              <Button 
                variant="outline"
                className="flex-1"
                onClick={() => toast.info('请查看岗位详情中的企业联系电话')}
              >
                <MessageSquare className="w-4 h-4 mr-2" />
                联系企业
              </Button>
              <Button 
                className="wx-btn-primary flex-1"
                onClick={onStartInterview}
              >
                <Video className="w-4 h-4 mr-2" />
                进入面试
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// 信息卡片
function InfoCard({ 
  icon, 
  label, 
  value, 
  bgColor 
}: { 
  icon: React.ReactNode; 
  label: string; 
  value: string;
  bgColor: string;
}) {
  return (
    <div className="flex items-center gap-3">
      <div className={`w-10 h-10 ${bgColor} rounded-lg flex items-center justify-center`}>
        {icon}
      </div>
      <div>
        <p className="text-sm text-gray-500">{label}</p>
        <p className="font-medium text-gray-800">{value}</p>
      </div>
    </div>
  );
}

// 检查项
function CheckItem({ text }: { text: string }) {
  return (
    <li className="flex items-start gap-2">
      <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
      {text}
    </li>
  );
}

// 声纹认证弹窗
interface VoiceAuthDialogProps {
  isOpen: boolean;
  onClose: () => void;
  step: 'intro' | 'recording' | 'success';
  onStartRecording: () => void;
  recordingTime: number;
}

function VoiceAuthDialog({ 
  isOpen, 
  onClose, 
  step, 
  onStartRecording,
  recordingTime 
}: VoiceAuthDialogProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-sm">
        {step === 'intro' && (
          <>
            <DialogHeader>
              <DialogTitle className="text-center">声纹身份认证</DialogTitle>
            </DialogHeader>
            <div className="text-center py-6">
              <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mic className="w-10 h-10 text-blue-600" />
              </div>
              <p className="text-gray-600 mb-2">
                面试前需要进行声纹认证
              </p>
              <p className="text-sm text-gray-500">
                请朗读屏幕上的随机数字，验证您的身份
              </p>
            </div>
            <div className="bg-gray-900 text-white p-4 rounded-xl text-center mb-4">
              <p className="text-3xl font-mono font-bold tracking-wider">3-8-2-9</p>
            </div>
            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={onClose}
              >
                取消
              </Button>
              <Button 
                className="wx-btn-primary flex-1"
                onClick={onStartRecording}
              >
                <Mic className="w-4 h-4 mr-2" />
                开始认证
              </Button>
            </div>
          </>
        )}

        {step === 'recording' && (
          <div className="text-center py-6">
            <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mic className="w-10 h-10 text-red-600" />
            </div>
            <p className="text-gray-600 mb-4">正在录音，请朗读数字</p>
            <div className="bg-gray-900 text-white p-4 rounded-xl text-center mb-4">
              <p className="text-3xl font-mono font-bold tracking-wider">3-8-2-9</p>
            </div>
            <Progress value={(recordingTime / 3) * 100} className="h-2" />
            <p className="text-sm text-gray-500 mt-2">
              {recordingTime.toFixed(1)}s / 3.0s
            </p>
          </div>
        )}

        {step === 'success' && (
          <div className="text-center py-6">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <p className="font-semibold text-gray-800 mb-1">认证成功</p>
            <p className="text-sm text-gray-500">正在进入面试房间...</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

// 空状态
function EmptyState({ type }: { type: 'upcoming' | 'completed' }) {
  const isUpcoming = type === 'upcoming';
  
  return (
    <div className="text-center py-12">
      <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
        {isUpcoming ? (
          <Calendar className="w-10 h-10 text-gray-400" />
        ) : (
          <CheckCircle className="w-10 h-10 text-gray-400" />
        )}
      </div>
      <p className="text-gray-500">
        {isUpcoming ? '暂无待面试' : '暂无已完成面试'}
      </p>
      {isUpcoming && (
        <p className="text-sm text-gray-400 mt-1">去岗位页面投递简历吧</p>
      )}
    </div>
  );
}

// 辅助函数
function getInterviewTypeIcon(type: Interview['type']) {
  switch (type) {
    case 'video': return <Video className="w-4 h-4" />;
    case 'voice': return <Mic className="w-4 h-4" />;
    case 'onsite': return <MapPin className="w-4 h-4" />;
  }
}

function getInterviewTypeText(type: Interview['type']) {
  switch (type) {
    case 'video': return '视频面试';
    case 'voice': return '语音面试';
    case 'onsite': return '现场面试';
  }
}
